import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext(null)

// Persist navigation state across tab switches
const NAV_STATE_KEY = 'c2_nav_state'
export function saveNavState(state) {
  try { sessionStorage.setItem(NAV_STATE_KEY, JSON.stringify(state)) } catch {}
}
export function getNavState() {
  try { return JSON.parse(sessionStorage.getItem(NAV_STATE_KEY)) } catch { return null }
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const initialLoadDone = useRef(false)
  const profileCache = useRef(null)
  const loadingProfile = useRef(false)

  const loadProfile = useCallback(async (userId) => {
    // Prevent duplicate loads
    if (loadingProfile.current) return
    // Use cached profile if same user
    if (profileCache.current?.id === userId) {
      setProfile(profileCache.current)
      setLoading(false)
      initialLoadDone.current = true
      return
    }
    loadingProfile.current = true
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single()
      if (error) throw error
      profileCache.current = data
      setProfile(data)
    } catch (err) {
      console.error('Profile load error:', err)
      setProfile(null)
      profileCache.current = null
    } finally {
      setLoading(false)
      initialLoadDone.current = true
      loadingProfile.current = false
    }
  }, [])

  useEffect(() => {
    // Initial session load
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s)
      if (s?.user) loadProfile(s.user.id)
      else { setLoading(false); initialLoadDone.current = true }
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        // TOKEN_REFRESHED: silent update, no loading, no profile reload
        if (event === 'TOKEN_REFRESHED') {
          setSession(newSession)
          return
        }
        // SIGNED_IN after initial load: just update session, use cached profile
        if (event === 'SIGNED_IN' && initialLoadDone.current) {
          setSession(newSession)
          if (newSession?.user && profileCache.current?.id === newSession.user.id) {
            return // Profile already cached, no reload needed
          }
          if (newSession?.user) loadProfile(newSession.user.id)
          return
        }
        // SIGNED_OUT
        if (event === 'SIGNED_OUT') {
          setSession(null)
          setProfile(null)
          profileCache.current = null
          setLoading(false)
          return
        }
        // Initial SIGNED_IN or other events
        setSession(newSession)
        if (newSession?.user) {
          if (!initialLoadDone.current) setLoading(true)
          loadProfile(newSession.user.id)
        } else {
          setProfile(null)
          setLoading(false)
        }
      }
    )
    return () => subscription.unsubscribe()
  }, [loadProfile])

  const signOut = useCallback(async () => {
    await supabase.auth.signOut()
    profileCache.current = null
    setProfile(null)
    setSession(null)
    sessionStorage.removeItem(NAV_STATE_KEY)
  }, [])

  return (
    <AuthContext.Provider value={{ session, profile, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
